<template>
  <div
    ref="containerRef"
    class="am3-toggle-button peer"
    :class="[
      stretch ? 'flex w-full' : 'inline-flex',
      isSlider
        ? 'relative gap-1 p-1 rounded-lg bg-surface'
        : ['rounded-md', containerSizeClasses],
      disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    ]"
    :data-test-id="generateDataTestId(dataTestId, 'toggle-button')"
  >
    <!-- Sliding white indicator (slider variant only) -->
    <div
      v-if="isSlider"
      class="absolute left-0 top-0 z-0 rounded-md bg-surface-light shadow-sm transition-all duration-300 ease-out pointer-events-none"
      :style="indicatorStyle"
      aria-hidden="true"
    />

    <button
      v-for="(option, index) in options"
      :key="index"
      :ref="(el) => setButtonRef(el, index)"
      class="flex flex-col relative gap-1 ring-0 outline-0 outline-none outline-transparent justify-center text-sm transition-colors duration-200 box-border"
      :class="[
        stretch ? 'flex-1' : '',
        buttonSizeClasses,
        getButtonTypeClasses(option.value === modelValue),
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        option.detail ? 'items-start' : 'items-center',
        isSlider
          ? 'z-10 rounded-md focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-surface-dark'
          : 'h-11 z-0 focus:z-30 first:rounded-l-md last:rounded-r-md border-surface-dark border-solid border-x border-y [&:not(:last-child)]:border-r-0 focus-visible:outline-transparent last:after:rounded-r-md first:after:rounded-l-md [&:not(:last-child)]:after:border-r-0 focus-within:outline-transparent after:absolute after:size-full after:focus:outline-surface-dark after:focus:outline-offset-2 after:z-20 after:outline-offset-2 after:outline after:outline-1 after:outline-transparent', // outline effects
      ]"
      :disabled="disabled"
      :aria-label="option.text"
      :data-test-id="generateDataTestId(dataTestId, `option-${option.text || option.value}`)"
      @click.stop="handleClick(option.value)"
    >
      <!-- Left icon if provided -->
      <div
        class="inline-flex items-center gap-1 "
      >
        <AM3Icon
          v-if="option.iconLeft"
          :key="`left-${option.iconLeft}`"
          :type="option.iconLeft"
          :size="option.iconSize"
          color="currentColor"
        />

        <!-- Button text and detail -->
        <span
          v-if="option.text"
          class="font-medium text-nowrap"
        >{{ option.text }}</span>
      </div>
      <div
        v-if="option.detail"
        class="text-left text-sm text-primary mt-0.5"
        :class="['primary', 'danger'].includes(type) ? 'text-white' : 'text-secondary'"
      >
        {{ option.detail }}
      </div>

      <!-- Right icon if provided -->
      <AM3Icon
        v-if="option.iconRight"
        :key="`right-${option.iconRight}`"
        :type="option.iconRight"
        :size="option.iconSize"
        color="currentColor"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type ComponentPublicInstance } from 'vue'
import AM3Icon, { type IconSize } from '@/components/am3-generic/base/icon/AM3Icon.vue'
import '@/components/am3-generic/core/theme.css'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'

export type ButtonType = 'default' | 'primary' | 'secondary' | 'tertiary' | 'danger' | 'filter' | 'slider'
export type ButtonSize = 'md' | 'sm' | 'xs'

export interface ToggleOption {
  text?: string
  detail?: string
  value: string | number
  iconLeft?: string
  iconRight?: string
  type?: ButtonType
  // set the display size of any icons used within the button
  iconSize?: IconSize
}

interface Props {
  // Options for toggle button parts
  options: ToggleOption[]
  // v-model value
  modelValue: string | number
  // Button type/style
  type?: ButtonType
  // Button size
  size?: ButtonSize
  // Disabled state
  disabled?: boolean
  // Data test id prefix
  dataTestId?: string
  // Whether the button should stretch to fill the container
  stretch?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'default',
  size: 'md',
  disabled: false,
  stretch: false,
  dataTestId: 'toggle-button',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number): void
}>()

// Slider variant: a single white "pill" that slides behind the active option.
const isSlider = computed(() => props.type === 'slider')

const containerRef = ref<HTMLElement | null>(null)
const buttonRefs = ref<HTMLButtonElement[]>([])

const setButtonRef = (el: Element | ComponentPublicInstance | null, index: number) => {
  if (el) {
    buttonRefs.value[index] = el as HTMLButtonElement
  }
}

const activeIndex = computed(() =>
  props.options.findIndex(option => option.value === props.modelValue),
)

// Inline style for the sliding indicator, measured from the active button's box.
const indicatorStyle = ref<Record<string, string>>({
  width: '0px',
  height: '0px',
  opacity: '0',
})

const updateIndicator = () => {
  if (!isSlider.value) return
  const button = buttonRefs.value[activeIndex.value]
  if (!button) {
    indicatorStyle.value = { ...indicatorStyle.value, opacity: '0' }
    return
  }
  indicatorStyle.value = {
    width: `${button.offsetWidth}px`,
    height: `${button.offsetHeight}px`,
    transform: `translate(${button.offsetLeft}px, ${button.offsetTop}px)`,
    opacity: '1',
  }
}

let resizeObserver: ResizeObserver | null = null

watch(
  () => [props.modelValue, props.options, props.size, props.stretch],
  () => nextTick(updateIndicator),
  { deep: true },
)

onMounted(() => {
  nextTick(updateIndicator)
  if (isSlider.value && containerRef.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => updateIndicator())
    resizeObserver.observe(containerRef.value)
  }
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

// Check if any option has detail
const hasDetail = computed(() => {
  return props.options.some(option => option.detail)
})

// Check if any option has detail
const hasText = computed(() => {
  return props.options.some(option => option.text)
})

// Compute button classes based on size
const buttonSizeClasses = computed(() => {
  const baseClasses = {
    xs: hasDetail.value ? 'min-h-14 px-2 py-1.5 text-xs' : 'h-[28px] px-2 py-1 text-xs',
    sm: hasDetail.value ? 'min-h-16 px-3 py-2 text-sm' : 'h-9 px-3 py-1.5 text-sm',
    md: hasDetail.value ? 'min-h-20 px-4 py-2.5 text-sm h-[46px]' : 'h-11 px-4 py-2 text-sm',
  }

  return baseClasses[props.size] || baseClasses.md
})

// Compute container classes based on size
const containerSizeClasses = computed(() => {
  const baseClasses = {
    xs: hasDetail.value ? 'min-h-12' : 'h-6',
    sm: hasDetail.value ? 'min-h-16' : 'h-9',
    md: hasDetail.value ? 'min-h-20' : 'h-[46px]',
  }

  return baseClasses[props.size] || baseClasses.md
})

// Get button type classes based on active state
const getButtonTypeClasses = computed(() => {
  return (isActive: boolean) => {
    const typeClasses = {
      default: {
        active: 'bg-surface-light text-brand ',
        inactive: 'bg-surface text-secondary hover:bg-brand-light ',
      },
      // slider: transparent buttons sitting above a sliding white indicator (see indicatorStyle)
      slider: {
        active: 'bg-transparent text-primary font-medium',
        inactive: 'bg-transparent text-secondary hover:text-primary',
      },
      // we're deprecating the below in AM3, but they are still here for backwards compatibility or edge cases if we need them.
      primary: {
        active: 'bg-[var(--buttons-primary-default)] text-white focus:outline-brand hover:bg-[var(--buttons-primary-hover)]',
        inactive: 'bg-[var(--buttons-secondary-default)] focus:outline-brand text-[var(--font-colour-brand)] hover:bg-[var(--buttons-secondary-hover)]',
      },
      secondary: {
        active: 'bg-[var(--buttons-secondary-hover)] focus:outline-surface-dark text-[var(--font-colour-brand)] border border-[var(--border-color-brand)] hover:bg-[var(--buttons-secondary-hover)]',
        inactive: 'bg-[var(--buttons-tertiary-default)] focus:outline-surface-dark text-[var(--font-colour-primary)] border border-[var(--border-color-strong)] hover:bg-[var(--buttons-tertiary-hover)]',
      },
      tertiary: {
        active: 'bg-[var(--buttons-tertiary-default)] focus:outline-surface-dark text-[var(--font-colour-primary)] border border-[var(--border-color-strong)] hover:bg-[var(--buttons-tertiary-hover)]',
        inactive: 'bg-transparent text-[var(--font-colour-primary)] focus:outline-surface-dark hover:bg-[var(--buttons-tertiary-hover)]',
      },
      danger: {
        active: 'bg-[var(--border-color-danger)] focus:outline-danger  text-white hover:bg-[var(--font-colour-danger)]',
        inactive: 'bg-[var(--buttons-tertiary-default)] focus:outline-danger  text-[var(--border-color-danger)] hover:bg-[var(--buttons-tertiary-hover)]',
      },
    }

    return isActive ? typeClasses[props.type].active : typeClasses[props.type].inactive
  }
})

// Handle click event
const handleClick = (value: string | number) => {
  if (!props.disabled) {
    emit('update:modelValue', value)
  }
}
</script>
